/**
 * LiveChatOverlay Component
 * Instagram Live-style chat overlay for mobile view
 * Transparent, fade-in messages pinned to bottom
 * Features: draggable resize, keyboard-sticky input, outside-click dismiss
 */

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LiveChatOverlay.css';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { MessageContent } from '../MessageContent';
import { VoiceRecordingBar } from '../VoiceRecordingBar/VoiceRecordingBar';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  tokenUsage?: {
    totalTokens: number;
    contextWindowSize: number;
    historyTruncated: boolean;
  };
}

interface LiveChatOverlayProps {
  messages: Message[];
  isVisible: boolean;
  onToggle: () => void;
  companionImage?: string;
  companionName?: string;
  onSendMessage?: (message: string) => void;
  isLoading?: boolean;
  isVoiceSupported?: boolean;
  isVoiceListening?: boolean;
  onToggleVoice?: () => void;
  onStopVoice?: () => void;
  onCancelVoice?: () => void;
  audioLevel?: number;
  isProcessing?: boolean;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  /** Called when user taps "Start Live Session" in the overlay input bar */
  onStartLiveSession?: () => void;
  isLiveSession?: boolean;
  onFocusChange?: (focused: boolean) => void;
  sttLanguage?: string;
  onSttLanguageChange?: (lang: string) => void;
}

export const LiveChatOverlay: React.FC<LiveChatOverlayProps> = ({
  messages,
  isVisible,
  onToggle,
  companionImage,
  companionName,
  onSendMessage,
  isLoading = false,
  isVoiceSupported = false,
  isVoiceListening = false,
  onToggleVoice,
  onStopVoice,
  onCancelVoice,
  audioLevel = 0,
  isProcessing = false,
  inputValue: externalInputValue,
  onInputChange: externalOnInputChange,
  onStartLiveSession,
  isLiveSession = false,
  onFocusChange,
  sttLanguage,
  onSttLanguageChange,
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [internalInputValue, setInternalInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  // Use external state if provided, otherwise internal
  const inputValue = externalInputValue !== undefined ? externalInputValue : internalInputValue;
  const setInputValue = (value: string) => {
    if (externalOnInputChange) {
      externalOnInputChange(value);
    } else {
      setInternalInputValue(value);
    }
  };

  /** Handle send message — always opens the panel, never closes it */
  const handleSend = () => {
    if (!inputValue.trim() || isLoading || !onSendMessage) return;
    // Always open (not toggle) so the overlay stays visible on subsequent sends
    if (!isVisible) onToggle();
    onSendMessage(inputValue.trim());
    setInputValue('');
  };

  /** Handle key press */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-open the messages panel whenever a new assistant reply arrives
  const prevMessagesLengthRef = useRef(messages.length);
  useEffect(() => {
    const hasNewAssistantMessage =
      messages.length > prevMessagesLengthRef.current &&
      messages[messages.length - 1]?.role === 'assistant';
    if (hasNewAssistantMessage && !isVisible) {
      onToggle();
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages, isVisible, onToggle]);

  // Auto-scroll to newest message whenever messages change or overlay becomes visible
  useEffect(() => {
    if (isVisible && messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isVisible]);

  // ---------- Outside-click dismiss ----------
  // Tapping outside the overlay wrapper closes the messages panel.
  // The input bar stays visible because it is inside overlayRef.
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      // Ignore if we are mid-drag (resize handle interaction)
      if (isDraggingResize.current) return;

      const target = e.target as Element;
      // Ignore clicks on the avatar controls sidebar to prevent double-toggle bugs
      if (target.closest('.avatar-controls') || target.closest('.heygen-avatar-controls-overlay')) {
        return;
      }

      if (overlayRef.current && !overlayRef.current.contains(target)) {
        onToggle(); // Close the messages panel only
      }
    };

    // Use capture phase so it fires before any child handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isVisible, onToggle]);

  // ---------- Resize handle logic ----------
  const [overlayHeight, setOverlayHeight] = useState(220);
  const isDraggingResize = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);

  const MIN_HEIGHT = 80;
  const MAX_HEIGHT = typeof window !== 'undefined' ? window.innerHeight * 0.65 : 500;

  /** Begins the resize interaction */
  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingResize.current = true;
      dragStartY.current = e.clientY;
      dragStartHeight.current = overlayHeight;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [overlayHeight],
  );

  /** Updates height while dragging (upward = taller) */
  const handleResizePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingResize.current) return;
      const delta = dragStartY.current - e.clientY;
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, dragStartHeight.current + delta));
      setOverlayHeight(newHeight);
    },
    [MAX_HEIGHT],
  );

  /** Ends the resize interaction */
  const handleResizePointerUp = useCallback(() => {
    isDraggingResize.current = false;
  }, []);

  return (
    <>
      {/* Chat overlay container — always visible; messages panel shown when isVisible */}
      <div
        ref={overlayRef}
        className={`live-chat-wrapper-root${inputFocused ? ' keyboard-open' : ''}`}
      >
        {/* Messages panel — shown/hidden based on isVisible */}
        {isVisible && (
          <div
            className="live-chat-overlay"
            style={{ height: `${overlayHeight}px`, maxHeight: `${MAX_HEIGHT}px` }}
          >
            {/* Animated drag-to-resize handle */}
            <div
              className="live-chat-resize-handle"
              onPointerDown={handleResizePointerDown}
              onPointerMove={handleResizePointerMove}
              onPointerUp={handleResizePointerUp}
              onPointerCancel={handleResizePointerUp}
            >
              <div className="live-chat-resize-bars">
                <span className="live-chat-resize-bar" />
                <span className="live-chat-resize-bar live-chat-resize-bar--mid" />
                <span className="live-chat-resize-bar" />
              </div>
            </div>

            <div className="live-chat-messages">
              {messages.length > 0 && (
                <>
                  {messages.slice(-15).map((msg) => (
                    <div key={msg.id} className={`live-chat-msg ${msg.role}`}>
                      {msg.role === 'assistant' && companionImage && (
                        <img
                          src={companionImage}
                          alt={companionName || 'Avatar'}
                          className="live-chat-avatar"
                        />
                      )}
                      <span className="live-chat-content">
                        <MessageContent
                          content={msg.content}
                          isAssistant={msg.role === 'assistant'}
                          companionName={msg.role === 'assistant' ? companionName : undefined}
                          onQuestionClick={onSendMessage}
                          disabled={isLoading}
                          inlineSuffix={
                            msg.tokenUsage ? (
                              <button
                                type="button"
                                className="live-chat-token-info-inline"
                                title={
                                  msg.tokenUsage.historyTruncated
                                    ? t('chat.tokenUsageTooltipFull', {
                                        total: msg.tokenUsage.totalTokens,
                                        size: msg.tokenUsage.contextWindowSize,
                                      })
                                    : t('chat.tokenUsageTooltip', {
                                        total: msg.tokenUsage.totalTokens,
                                        size: msg.tokenUsage.contextWindowSize,
                                      })
                                }
                                style={{
                                  marginLeft: '6px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  verticalAlign: 'middle',
                                  color: msg.tokenUsage.historyTruncated
                                    ? '#eab308'
                                    : 'rgba(255, 255, 255, 0.4)',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  outline: 'none',
                                }}
                                onClick={(e) => {
                                  // On mobile, this helps trigger the title tooltip reliably without an alert
                                  e.preventDefault();
                                }}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  aria-hidden="true"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="16" x2="12" y2="12" />
                                  <line x1="12" y1="8" x2="12.01" y2="8" />
                                </svg>
                              </button>
                            ) : null
                          }
                        />
                      </span>
                    </div>
                  ))}
                  {/* Thinking bubble while waiting for reply */}
                  {isLoading && (
                    <div className="live-chat-msg assistant live-chat-thinking">
                      {companionImage && (
                        <img
                          src={companionImage}
                          alt={companionName || 'Avatar'}
                          className="live-chat-avatar"
                        />
                      )}
                      <span className="live-chat-content live-chat-thinking-dots">
                        <span />
                        <span />
                        <span />
                      </span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Input bar — always pinned at bottom, attaches to keyboard */}
        {onSendMessage && (
          <div
            className={`live-chat-input-container${isVoiceListening ? ' recording' : ''}${inputFocused ? ' keyboard-open' : ''}`}
            style={
              isVoiceListening
                ? {
                    border: '1px solid rgba(196, 154, 108, 0.6)',
                    boxShadow: '0 0 12px rgba(196, 154, 108, 0.25)',
                  }
                : {}
            }
          >
            {isVoiceListening || (isProcessing && !isVoiceListening) ? (
              <VoiceRecordingBar
                variant="chatbox"
                isProcessing={isProcessing}
                audioLevel={audioLevel}
                onCancel={() => onCancelVoice?.()}
                onConfirm={() => onStopVoice?.()}
              />
            ) : (
              <div className="live-chat-input-wrapper">
                <div className="live-chat-input-top-row">
                  <textarea
                    ref={inputRef}
                    className="live-chat-input"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      isLoading
                        ? t('avatar.thinking', 'Thinking...')
                        : t('avatar.typeMessage', 'Type a message...')
                    }
                    rows={1}
                    disabled={isLoading}
                    onFocus={() => {
                      setInputFocused(true);
                      onFocusChange?.(true);
                      // Always open (not toggle) so focusing never accidentally hides the panel
                      if (!isVisible) onToggle();
                    }}
                    onBlur={() => {
                      setInputFocused(false);
                      onFocusChange?.(false);
                    }}
                  />

                  {inputValue.trim() && (
                    <button
                      type="button"
                      className="live-chat-send-btn"
                      onClick={handleSend}
                      disabled={isLoading}
                      aria-label={t('chat.send', 'Send')}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>{t('chat.send', 'Send')}</title>
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="live-chat-input-bottom-row">
                  {onSttLanguageChange && sttLanguage && (
                    <LanguageSelector
                      value={sttLanguage}
                      onChange={onSttLanguageChange}
                      variant="mobile"
                      className="live-chat-lang-selector"
                      disabled={false}
                    />
                  )}

                  <div className="live-chat-input-actions">
                    {/* Voice Button */}
                    {onToggleVoice && isVoiceSupported && (
                      <button
                        type="button"
                        className="live-chat-voice-btn"
                        onClick={onToggleVoice}
                        title={t('chat.startListening', 'Tap to speak')}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <title>{t('chat.voiceInput', 'Voice Input')}</title>
                          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                          <line x1="12" y1="19" x2="12" y2="23" />
                          <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                      </button>
                    )}

                    {/* Start Live Session button — shown when not in live session */}
                    {!isLiveSession && onStartLiveSession && (
                      <button
                        type="button"
                        className="live-chat-start-live-btn"
                        onClick={onStartLiveSession}
                        aria-label={t('chat.startLiveSession', 'Start Live Session')}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                        <span>{t('chat.live', 'LIVE')}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default LiveChatOverlay;
